'use client';

import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, FlaskRound } from 'lucide-react';
import { useDesign } from '@/context/DesignContext';

interface ParapharmaFooterProps {
  brandName?: string;
  tagline?: string;
  description?: string;
  socialLinks?: Array<{
    name: string;
    url: string;
  }>;
  contactInfo?: {
    phone?: string;
    email?: string;
    address?: string;
    hours?: string;
  };
  productLinks?: string[];
  serviceLinks?: string[];
  design?: any;
}

export function ParapharmaFooter({
  brandName = 'ParaPharma',
  tagline = 'Premium',
  description = 'Votre parapharmacie de confiance pour tous vos besoins santé et bien-être depuis 2014.',
  socialLinks = [
    { name: 'Facebook', url: '#' },
    { name: 'Instagram', url: '#' },
    { name: 'Twitter', url: '#' }
  ],
  contactInfo = {
    phone: '01 23 45 67 89',
    email: 'contact@parapharma.fr',
    address: '123 Rue de Paris, 75001',
    hours: 'Lun-Sam: 9h-19h'
  },
  productLinks = ['Soins Visage', 'Compléments', 'Hygiène', 'Bébé', 'Solaire'],
  serviceLinks = ['Nous Contacter', 'FAQ', 'Livraison', 'Retours', 'Mentions légales'],
  design: propDesign
}: ParapharmaFooterProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const bgColor = design?.componentDesignMap?.ParapharmaFooter?.background || '#0F172A';
  const textColor = design?.componentDesignMap?.ParapharmaFooter?.textColor || '#94A3B8';
  const titleColor = design?.componentDesignMap?.ParapharmaFooter?.titleColor || '#FFFFFF';
  const linkHoverColor = design?.componentDesignMap?.ParapharmaFooter?.linkHoverColor || '#FFFFFF';
  const brandIconBg = design?.componentDesignMap?.ParapharmaFooter?.brandIconBackground || 'from-slate-700 to-slate-800';
  const brandIconColor = design?.componentDesignMap?.ParapharmaFooter?.brandIconColor || '#FFFFFF';
  const brandNameColor = design?.componentDesignMap?.ParapharmaFooter?.brandNameColor || '#FFFFFF';
  const taglineColor = design?.componentDesignMap?.ParapharmaFooter?.taglineColor || '#94A3B8';
  const socialIconBg = design?.componentDesignMap?.ParapharmaFooter?.socialIconBackground || '#1E293B';
  const socialIconHoverBg = design?.componentDesignMap?.ParapharmaFooter?.socialIconHoverBackground || '#334155';
  const borderColor = design?.componentDesignMap?.ParapharmaFooter?.borderColor || '#1E293B';
  const copyrightColor = design?.componentDesignMap?.ParapharmaFooter?.copyrightColor || '#64748B';

  return (
    <footer className="py-16" style={{ background: bgColor, color: textColor }}>
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 bg-gradient-to-br ${brandIconBg} rounded-xl flex items-center justify-center`}>
                <FlaskRound className="w-5 h-5" style={{ color: brandIconColor }} />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ color: brandNameColor }}>{brandName}</h3>
                <p className="text-xs tracking-widest uppercase" style={{ color: taglineColor }}>{tagline}</p>
              </div>
            </div>
            <p className="leading-relaxed mb-6" style={{ color: textColor }}>{description}</p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  whileHover={{ scale: 1.1 }}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: socialIconBg }}
                  onMouseEnter={(e) => e.currentTarget.style.background = socialIconHoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.background = socialIconBg}
                >
                  <span className="text-xs font-bold">{social.name[0]}</span>
                </motion.a>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ color: titleColor }}>Produits</h4>
            <ul className="space-y-3">
              {productLinks.map((item) => (
                <li key={item}>
                  <a href="#" className="transition-colors" style={{ color: textColor }} onMouseEnter={(e) => e.currentTarget.style.color = linkHoverColor} onMouseLeave={(e) => e.currentTarget.style.color = textColor}>{item}</a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ color: titleColor }}>Service Client</h4>
            <ul className="space-y-3">
              {serviceLinks.map((item) => (
                <li key={item}>
                  <a href="#" className="transition-colors" style={{ color: textColor }} onMouseEnter={(e) => e.currentTarget.style.color = linkHoverColor} onMouseLeave={(e) => e.currentTarget.style.color = textColor}>{item}</a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-lg mb-6" style={{ color: titleColor }}>Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5" style={{ color: textColor }} />
                <span>{contactInfo.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5" style={{ color: textColor }} />
                <span>{contactInfo.email}</span>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5" style={{ color: textColor }} />
                <span>{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5" style={{ color: textColor }} />
                <span>{contactInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderColor: borderColor }}>
          <p className="text-sm" style={{ color: copyrightColor }}>© 2024 {brandName} {tagline}. Tous droits réservés.</p>
          <div className="flex gap-6 text-sm" style={{ color: copyrightColor }}>
            <a href="#" className="hover:text-white transition-colors" style={{ color: copyrightColor }} onMouseEnter={(e) => e.currentTarget.style.color = linkHoverColor} onMouseLeave={(e) => e.currentTarget.style.color = copyrightColor}>Confidentialité</a>
            <a href="#" className="hover:text-white transition-colors" style={{ color: copyrightColor }} onMouseEnter={(e) => e.currentTarget.style.color = linkHoverColor} onMouseLeave={(e) => e.currentTarget.style.color = copyrightColor}>CGV</a>
            <a href="#" className="hover:text-white transition-colors" style={{ color: copyrightColor }} onMouseEnter={(e) => e.currentTarget.style.color = linkHoverColor} onMouseLeave={(e) => e.currentTarget.style.color = copyrightColor}>Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
}